/**
 * WUA Fast Match — screen components.
 *
 * One component per interaction shape called for by
 * `04_UX_AND_COPY_SPEC.md`. Every visual is driven by release-provided
 * copy — no wording lives in this file. Cards use accessible
 * checkbox/radio semantics (WCAG 2.2 AA) rather than <select>.
 *
 * Screens:
 *   Q01GroupedMulti         — 21 topic cards in 5 accordion groups (2–5)
 *   FilteredMulti           — Q02 (must-have from Q01), Q03 (avoid; excludes Q01/Q02)
 *   FixedMulti              — Q04–Q06 (exactly 2), Q12 (exactly 2)
 *   SingleChoice            — Q07–Q09 (exactly 1), Q13 (exactly 1)
 *   OptionalMultiWithNone   — Q10, Q11 (up to 2 OR "None of these")
 *   VignettePair            — Q14 (opaque left/right + Both/Neither)
 *
 * All external interactivity flows through the `onChange(optionIds)`
 * callback and the parent's `Continue`. This keeps every screen a
 * controlled component; state lives one level up.
 */
import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

const Card = ({ selected, disabled, onClick, testId, role, ariaChecked, ariaLabel, children }) => (
  <button
    type="button"
    role={role || "checkbox"}
    aria-checked={ariaChecked}
    aria-label={ariaLabel}
    aria-disabled={disabled || undefined}
    onClick={disabled ? undefined : onClick}
    data-testid={testId}
    className={cn(
      "w-full rounded-lg border px-4 py-3 text-left transition-colors",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2",
      "min-h-[56px] flex items-start gap-3",
      selected
        ? "border-slate-900 bg-slate-900 text-white"
        : disabled
          ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
          : "border-slate-300 bg-white text-slate-900 hover:border-slate-500",
    )}
  >
    <span
      aria-hidden="true"
      className={cn(
        "mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border",
        selected ? "border-white bg-white" : "border-slate-400 bg-white",
      )}
    >
      {selected && (
        <svg viewBox="0 0 20 20" className="h-3 w-3 fill-slate-900" aria-hidden="true">
          <path d="M7.5 13.6 3.9 10l-1.4 1.4L7.5 16.4 17.5 6.4l-1.4-1.4z" />
        </svg>
      )}
    </span>
    <span className="flex-1">{children}</span>
  </button>
);

/* ------------------------------------------------------------------ */
/* Q01 — five collapsible topic groups, 2–5 total selections           */
/* ------------------------------------------------------------------ */
export function Q01GroupedMulti({ question, topicGroups, value, onChange }) {
  const [openGroups, setOpenGroups] = useState(() =>
    // Owner-approved Q01 refinement: start every subject area
    // collapsed. Each area can still be expanded/collapsed
    // independently via the existing toggle. Selected options survive
    // collapse (the checked state lives in `value`, not in this map).
    Object.fromEntries(topicGroups.map((g) => [g.groupId, false])),
  );
  const byOptionId = useMemo(() => {
    const m = new Map();
    (question.options || []).forEach((o) => m.set(o.optionId, o));
    return m;
  }, [question]);
  const selected = new Set(value || []);
  const atMax = selected.size >= question.maxSelections;

  const toggle = (oid) => {
    const next = new Set(selected);
    if (next.has(oid)) next.delete(oid);
    else if (!atMax) next.add(oid);
    onChange([...next]);
  };

  return (
    <div className="space-y-3">
      {topicGroups.map((g) => {
        const open = openGroups[g.groupId];
        const groupSelectedCount = g.q01OptionIds.filter((oid) => selected.has(oid)).length;
        return (
          <div key={g.groupId} className="rounded-lg border border-slate-200 bg-white">
            <button
              type="button"
              onClick={() => setOpenGroups((s) => ({ ...s, [g.groupId]: !s[g.groupId] }))}
              aria-expanded={open}
              aria-controls={`topic-group-${g.groupId}`}
              data-testid={`q01-group-${g.groupId}-toggle`}
              className="flex w-full items-center justify-between rounded-t-lg px-4 py-3 text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
            >
              <span className="text-sm font-medium text-slate-900">
                {g.label}
                {groupSelectedCount > 0 && (
                  <span className="ml-2 text-xs text-slate-500" data-testid={`q01-group-${g.groupId}-count`}>
                    ({groupSelectedCount} selected)
                  </span>
                )}
              </span>
              <ChevronDown
                className={cn("h-4 w-4 text-slate-500 transition-transform", open && "rotate-180")}
                aria-hidden="true"
              />
            </button>
            {open && (
              <div
                id={`topic-group-${g.groupId}`}
                role="group"
                aria-label={g.label}
                className="space-y-2 border-t border-slate-100 p-3"
              >
                {g.q01OptionIds.map((oid) => {
                  const o = byOptionId.get(oid);
                  if (!o) return null;
                  const isSel = selected.has(oid);
                  return (
                    <Card
                      key={oid}
                      selected={isSel}
                      disabled={!isSel && atMax}
                      onClick={() => toggle(oid)}
                      role="checkbox"
                      ariaChecked={isSel}
                      testId={`q01-option-${o.optionCode}`}
                    >
                      <span className="text-sm font-medium">{o.displayText}</span>
                      {o.example && (
                        <span className={cn("mt-1 block text-xs", isSel ? "text-slate-200" : "text-slate-500")}>
                          {o.example}
                        </span>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Filtered / exclusive multi-select                                   */
/* Used by Q02 (must be in Q01 topics) and Q03 (must NOT be in Q01+Q02) */
/* ------------------------------------------------------------------ */
export function FilteredMulti({ question, includeOptionIds, excludeOptionCodes, value, onChange,
                                allowNoneOptionCode, noneLabel }) {
  const selected = new Set(value || []);
  const opts = (question.options || []).filter((o) => {
    if (includeOptionIds && !includeOptionIds.has(o.optionId)) return false;
    if (excludeOptionCodes && excludeOptionCodes.has(o.optionCode)) return false;
    return true;
  });
  const atMax = selected.size >= question.maxSelections;
  const toggle = (oid, isSingle) => {
    const next = new Set(selected);
    if (isSingle) {
      onChange(next.has(oid) ? [] : [oid]);
      return;
    }
    if (next.has(oid)) next.delete(oid);
    else if (!atMax) next.add(oid);
    onChange([...next]);
  };
  const isSingle = question.minSelections === 1 && question.maxSelections === 1;
  return (
    <div role={isSingle ? "radiogroup" : "group"} aria-label={question.screenTitle} className="space-y-2">
      {opts.map((o) => {
        const isSel = selected.has(o.optionId);
        return (
          <Card
            key={o.optionId}
            selected={isSel}
            disabled={!isSel && !isSingle && atMax}
            onClick={() => toggle(o.optionId, isSingle)}
            role={isSingle ? "radio" : "checkbox"}
            ariaChecked={isSel}
            testId={`${question.questionId.toLowerCase()}-option-${o.optionCode}`}
          >
            <span className="text-sm font-medium">{o.displayText}</span>
            {o.example && (
              <span className={cn("mt-1 block text-xs", isSel ? "text-slate-200" : "text-slate-500")}>
                {o.example}
              </span>
            )}
          </Card>
        );
      })}
      {allowNoneOptionCode && (
        <Card
          selected={false}
          onClick={() => onChange([])}
          role="button"
          ariaChecked={false}
          testId={`${question.questionId.toLowerCase()}-none`}
          ariaLabel={noneLabel}
        >
          <span className="text-sm font-medium">{noneLabel}</span>
        </Card>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Fixed multi-select (exactly N)  → Q04, Q05, Q06, Q12               */
/* ------------------------------------------------------------------ */
export function FixedMulti({ question, value, onChange }) {
  const selected = new Set(value || []);
  const atMax = selected.size >= question.maxSelections;
  const toggle = (oid) => {
    const next = new Set(selected);
    if (next.has(oid)) next.delete(oid);
    else if (!atMax) next.add(oid);
    onChange([...next]);
  };
  return (
    <div role="group" aria-label={question.screenTitle} className="space-y-2">
      {(question.options || []).map((o) => {
        const isSel = selected.has(o.optionId);
        return (
          <Card
            key={o.optionId}
            selected={isSel}
            disabled={!isSel && atMax}
            onClick={() => toggle(o.optionId)}
            role="checkbox"
            ariaChecked={isSel}
            testId={`${question.questionId.toLowerCase()}-option-${o.optionCode}`}
          >
            <span className="text-sm font-medium">{o.displayText}</span>
            {o.example && (
              <span className={cn("mt-1 block text-xs", isSel ? "text-slate-200" : "text-slate-500")}>
                {o.example}
              </span>
            )}
          </Card>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Single-choice → Q07, Q08, Q09, Q13                                 */
/* ------------------------------------------------------------------ */
export function SingleChoice({ question, value, onChange }) {
  const cur = (value && value[0]) || null;
  return (
    <div role="radiogroup" aria-label={question.screenTitle} className="space-y-2">
      {(question.options || []).map((o) => {
        const isSel = cur === o.optionId;
        return (
          <Card
            key={o.optionId}
            selected={isSel}
            onClick={() => onChange([o.optionId])}
            role="radio"
            ariaChecked={isSel}
            testId={`${question.questionId.toLowerCase()}-option-${o.optionCode}`}
          >
            <span className="text-sm font-medium">{o.displayText}</span>
            {o.example && (
              <span className={cn("mt-1 block text-xs", isSel ? "text-slate-200" : "text-slate-500")}>
                {o.example}
              </span>
            )}
          </Card>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Optional multi + mutually-exclusive "None"  → Q10, Q11             */
/* ------------------------------------------------------------------ */
export function OptionalMultiWithNone({ question, value, onChange, noneLabel }) {
  const selected = new Set(value || []);
  const noneSelected = selected.has("__NONE__");
  const atMax = selected.size >= question.maxSelections && !noneSelected;
  const toggle = (oid) => {
    if (noneSelected) {
      onChange([oid]);
      return;
    }
    const next = new Set(selected);
    if (next.has(oid)) next.delete(oid);
    else if (!atMax) next.add(oid);
    onChange([...next]);
  };
  const toggleNone = () => {
    if (noneSelected) onChange([]);
    else onChange(["__NONE__"]);
  };
  return (
    <div role="group" aria-label={question.screenTitle} className="space-y-2">
      {(question.options || []).map((o) => {
        const isSel = selected.has(o.optionId);
        return (
          <Card
            key={o.optionId}
            selected={isSel}
            disabled={!isSel && (atMax || noneSelected)}
            onClick={() => toggle(o.optionId)}
            role="checkbox"
            ariaChecked={isSel}
            testId={`${question.questionId.toLowerCase()}-option-${o.optionCode}`}
          >
            <span className="text-sm font-medium">{o.displayText}</span>
            {o.example && (
              <span className={cn("mt-1 block text-xs", isSel ? "text-slate-200" : "text-slate-500")}>
                {o.example}
              </span>
            )}
          </Card>
        );
      })}
      <Card
        selected={noneSelected}
        onClick={toggleNone}
        role="checkbox"
        ariaChecked={noneSelected}
        testId={`${question.questionId.toLowerCase()}-none`}
      >
        <span className="text-sm font-medium">{noneLabel}</span>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Q14 vignette pair — server-provided, opaque                         */
/* ------------------------------------------------------------------ */
export function VignettePair({ offer, choice, onChoose }) {
  return (
    <div className="space-y-4">
      <div role="radiogroup" aria-label="Choose the study experience that fits you better" className="grid gap-3 sm:grid-cols-2">
        {["left", "right"].map((side) => (
          <button
            key={side}
            type="button"
            role="radio"
            aria-checked={choice === side}
            onClick={() => onChoose(side)}
            data-testid={`q14-vignette-${side}`}
            className={cn(
              "flex min-h-[180px] flex-col rounded-lg border p-5 text-left transition-colors",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2",
              choice === side
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-300 bg-white text-slate-900 hover:border-slate-500",
            )}
          >
            <span className={cn("mb-3 inline-flex h-6 w-6 items-center justify-center rounded-full border text-xs font-medium",
              choice === side ? "border-white bg-white text-slate-900" : "border-slate-400 bg-white text-slate-700")}>
              {side === "left" ? "A" : "B"}
            </span>
            <span className="text-sm leading-relaxed">
              {offer[side]?.vignette || "…"}
            </span>
          </button>
        ))}
      </div>
      <div role="radiogroup" aria-label="Or pick a shared option" className="grid gap-2 sm:grid-cols-2">
        {(offer.controls || []).map((c) => (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={choice === c.id}
            onClick={() => onChoose(c.id)}
            data-testid={`q14-control-${c.id}`}
            className={cn(
              "rounded-lg border px-4 py-3 text-left text-sm transition-colors",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2",
              choice === c.id
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-300 bg-white text-slate-900 hover:border-slate-500",
            )}
          >
            {c.label}
          </button>
        ))}
      </div>
    </div>
  );
}
